import * as React from "react";
import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";

const ViewOrgs = ({
  orgs = [],
  tree = [],
  limit = 2,
  title = "Organizations",
  searchKeyWord = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const findPath = (nodes = [], targetId, path = []) => {
    for (const node of nodes) {
      const newPath = [
        ...path,
        {
          value: node.id,
          label: node.name,
          status: node.status,
          inactiveDate: node.inactiveDate,
        },
      ];

      if (node.id === targetId) {
        return newPath;
      }

      if (node.children?.length) {
        const result = findPath(node.children, targetId, newPath);
        if (result) return result;
      }
    }
    return null;
  };
  const keyword = String(searchKeyWord || "")
    .toLowerCase()
    .trim();

  const getScore = (name = "") => {
    if (!keyword) return 0;

    const lower = String(name).toLowerCase();

    if (lower === keyword) return 4;
    if (lower.startsWith(keyword)) return 3;
    if (lower.split(/\s+/).some((word) => word.startsWith(keyword))) return 2;
    if (lower.includes(keyword)) return 1;

    return 0;
  };

  const safeOrgs = Array.isArray(orgs)
    ? orgs
        .map((org, index) => {
          const chain = findPath(tree, org.id) || [
            { value: org.id, label: org.name },
          ];

          return {
            id: org.id,
            name: org.name,
            chain,
            _score: getScore(org.name),
            _index: index,
          };
        })
        .sort((a, b) => {
          if (!keyword) return a._index - b._index;

          if (a._score !== b._score) {
            return b._score - a._score;
          }

          return a._index - b._index;
        })
        .map(({ _score, _index, ...org }) => org)
    : [];

  const visibleOrgs = safeOrgs.slice(0, limit);
  const hiddenCount = Math.max(0, safeOrgs.length - limit);

  if (safeOrgs.length === 0) {
    return (
      <span className="text-xs text-muted-foreground italic">
        No organizations
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Visible Badges */}
      {visibleOrgs.map((org) => (
        <div key={org.id} className="flex items-center gap-1">
          <Badge className="bg-[hsl(var(--target-badge))] text-[hsl(var(--target-badge-foreground))] border-transparent">
            {org.name}
          </Badge>
          {org.status && (
            <OrgStatusTag
              status={org.status}
              inactiveDate={org.inactiveDate}
              className="text-[10px]"
            />
          )}
        </div>
      ))}

      {/* Hidden Popover */}
      {hiddenCount > 0 && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Badge className="cursor-pointer bg-[hsl(var(--target-badge))] text-[hsl(var(--target-badge-foreground))] hover:bg-[hsl(var(--target-badge-hover))] transition-colors">
              +{hiddenCount}
            </Badge>
          </PopoverTrigger>
          <PopoverContent
            forceMount
            align="end"
            side="bottom"
            sideOffset={8}
            collisionPadding={12}
            className="w-[min(32rem,calc(100vw-1rem))] rounded-2xl border-border bg-card p-0 text-foreground shadow-lg overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40">
              <span className="text-[11px] font-bold uppercase tracking-widest">
                {title}
              </span>
              <Button
                variant="ghost"
                size={"xs"}
                onClick={() => setIsOpen(false)}
                className={"uppercase text-[10px] font-bold "}
              >
                Close
              </Button>
            </div>

            {/* Body */}
            <div className="p-4">
              <div
                className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-2 custom-scrollbar overscroll-contain"
                onWheel={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {safeOrgs.map((org) => (
                  <div
                    key={org.id}
                    className="flex min-w-0 items-center gap-2 rounded border bg-muted/20 px-2 py-1"
                  >
                    {/* Icon */}
                    <div className="flex shrink-0 text-blue-600">
                      <GitBranch className="h-3 w-3" />
                    </div>

                    {/* Breadcrumb */}
                    <div className="min-w-0 flex-1 text-xs leading-5 break-words flex items-center gap-0.5 flex-wrap">
                      {org.chain.map((item, index) => {
                        const isLast = index === org.chain.length - 1;

                        return (
                          <React.Fragment
                            key={`${org.id}-${item.value}-${index}`}
                          >
                            {index > 0 && (
                              <span className="px-1 text-muted-foreground/50 font-bold">
                                {">"}
                              </span>
                            )}

                            <div className="flex items-center gap-0.5">
                              <span
                                className={
                                  isLast
                                    ? "font-semibold text-blue-600"
                                    : "text-muted-foreground"
                                }
                              >
                                {item.label}
                              </span>
                              <OrgStatusTag
                                status={item.status}
                                inactiveDate={item.inactiveDate}
                                className="text-[10px]"
                              />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Scrollbar styling */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .custom-scrollbar {
              scrollbar-width: thin;
              scrollbar-color: hsl(var(--muted-foreground)) transparent;
            }
            .custom-scrollbar::-webkit-scrollbar {
              width: 4px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: transparent;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: hsl(var(--muted-foreground));
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: hsl(var(--foreground));
            }
          `,
        }}
      />
    </div>
  );
};

export default ViewOrgs;
